import React, { useState, useEffect } from "react";
import { X, Anchor, MessageCircle, Check } from "lucide-react";
import { CONTACT } from "../data";

const BED_COLORS = {
  free: "#1FA866",
  occupied: "#E05548",
  male: "#3B82F6",
  female: "#EC4899",
  freeStroke: "#0F6E3F",
  occupiedStroke: "#8A2F27",
  maleStroke: "#1E40AF",
  femaleStroke: "#9F1239",
};

const bedFill = (value) => {
  if (value === "M") return { fill: BED_COLORS.male, stroke: BED_COLORS.maleStroke };
  if (value === "F") return { fill: BED_COLORS.female, stroke: BED_COLORS.femaleStroke };
  if (value) return { fill: BED_COLORS.occupied, stroke: BED_COLORS.occupiedStroke };
  return { fill: BED_COLORS.free, stroke: BED_COLORS.freeStroke };
};

const cabinGenderOf = (arr) => {
  const g = arr.map((b) => (b === "M" || b === "F" ? b : null)).filter(Boolean);
  if (g.length === 0) return null;
  const set = new Set(g);
  return set.size === 1 ? g[0] : "MIXED";
};

const GENDER_LABEL = {
  M: { one: "masculina", many: "masculinas" },
  F: { one: "feminina", many: "femininas" },
};

function Bed({ x, y, w, h, value, label }) {
  const { fill, stroke } = bedFill(value);
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="3" fill={fill} stroke={stroke} strokeWidth="1.5" />
      {label && (
        <text x={x + w / 2} y={y + h / 2 + 3} fontSize="8" textAnchor="middle" fill="#fff" fontWeight="700">
          {label}
        </text>
      )}
    </g>
  );
}

function FloorPlan({ occupancy }) {
  const occ = occupancy || {};
  const cabineCasal = occ.cabineCasal || [false, false];
  const suiteTripla = occ.suiteTripla || [false, false, false];
  const suiteCasal = occ.suiteCasal || [false, false];
  const cabineQuadrupla = occ.cabineQuadrupla || [false, false, false, false];

  const casalOccupancyState = (bedsArr) => bedsArr.filter(Boolean).length >= 1;

  return (
    <svg viewBox="0 0 400 500" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "auto", background: "#fff", border: "1px solid #E5E9F0" }}>
      <path d="M 30 40 Q 20 250 60 470 L 340 470 Q 380 250 370 40 Z" fill="#F5F8FC" stroke="#0F1B33" strokeWidth="1.5" />
      <path d="M 8 60 Q 0 260 20 460 L 60 470 Q 20 250 30 40 Z" fill="#EBF1F8" stroke="#0F1B33" strokeWidth="1" />
      <path d="M 392 60 Q 400 260 380 460 L 340 470 Q 380 250 370 40 Z" fill="#EBF1F8" stroke="#0F1B33" strokeWidth="1" />
      <text x="200" y="20" fontSize="9" textAnchor="middle" fill="#0F1B33" fontWeight="700" letterSpacing="2">PROA</text>
      <text x="200" y="492" fontSize="9" textAnchor="middle" fill="#0F1B33" fontWeight="700" letterSpacing="2">POPA</text>

      <rect x="55" y="40" width="115" height="90" fill="#fff" stroke="#0F1B33" strokeWidth="1.2" />
      <text x="112" y="54" fontSize="8" textAnchor="middle" fill="#0F1B33" fontWeight="700" letterSpacing="1">CABINE CASAL</text>
      <Bed x={68} y={62} w={90} h={56} value={casalOccupancyState(cabineCasal)} label={`${cabineCasal.filter(Boolean).length}/2`} />

      <rect x="175" y="40" width="35" height="35" fill="#EBF1F8" stroke="#0F1B33" strokeWidth="1" />
      <text x="192" y="60" fontSize="8" textAnchor="middle" fill="#0F1B33" fontWeight="700">WC</text>
      <rect x="215" y="40" width="35" height="35" fill="#EBF1F8" stroke="#0F1B33" strokeWidth="1" />
      <text x="232" y="60" fontSize="8" textAnchor="middle" fill="#0F1B33" fontWeight="700">WC</text>

      <rect x="255" y="40" width="90" height="115" fill="#fff" stroke="#0F1B33" strokeWidth="1.2" />
      <text x="300" y="54" fontSize="8" textAnchor="middle" fill="#0F1B33" fontWeight="700" letterSpacing="1">SUÍTE TRIPLA</text>
      <Bed x={262} y={62} w={35} h={30} value={suiteTripla[0]} label="1" />
      <Bed x={303} y={62} w={35} h={30} value={suiteTripla[1]} label="2" />
      <Bed x={262} y={97} w={76} h={50} value={suiteTripla[2]} label="3" />

      <rect x="80" y="160" width="240" height="180" fill="#FFFBF0" stroke="#0F1B33" strokeWidth="1" strokeDasharray="4,3" />
      <text x="140" y="220" fontSize="11" textAnchor="middle" fill="#0F1B33" fontWeight="700" letterSpacing="2">SALA</text>
      <text x="270" y="300" fontSize="10" textAnchor="middle" fill="#0F1B33" fontWeight="700" letterSpacing="2">COZINHA</text>
      <path d="M 200 220 Q 240 230 260 260 Q 275 300 250 320" fill="none" stroke="#0F1B33" strokeWidth="1.2" />

      <rect x="175" y="425" width="35" height="35" fill="#EBF1F8" stroke="#0F1B33" strokeWidth="1" />
      <text x="192" y="445" fontSize="8" textAnchor="middle" fill="#0F1B33" fontWeight="700">WC</text>
      <rect x="215" y="425" width="35" height="35" fill="#EBF1F8" stroke="#0F1B33" strokeWidth="1" />
      <text x="232" y="445" fontSize="8" textAnchor="middle" fill="#0F1B33" fontWeight="700">WC</text>

      <rect x="55" y="365" width="115" height="95" fill="#fff" stroke="#0F1B33" strokeWidth="1.2" />
      <text x="112" y="379" fontSize="8" textAnchor="middle" fill="#0F1B33" fontWeight="700" letterSpacing="1">SUÍTE CASAL</text>
      <Bed x={68} y={387} w={90} h={60} value={casalOccupancyState(suiteCasal)} label={`${suiteCasal.filter(Boolean).length}/2`} />

      <rect x="255" y="365" width="90" height="95" fill="#fff" stroke="#0F1B33" strokeWidth="1.2" />
      <text x="300" y="379" fontSize="8" textAnchor="middle" fill="#0F1B33" fontWeight="700" letterSpacing="1">CABINE QUÁDRUPLA</text>
      <line x1="300" y1="385" x2="300" y2="458" stroke="#0F1B33" strokeWidth="0.6" strokeDasharray="2,2" opacity="0.4" />
      <text x="280" y="391" fontSize="6" textAnchor="middle" fill="#5A6B85" fontWeight="700">A</text>
      <text x="320" y="391" fontSize="6" textAnchor="middle" fill="#5A6B85" fontWeight="700">B</text>
      <Bed x={262} y={395} w={35} h={28} value={cabineQuadrupla[0]} label="1" />
      <Bed x={303} y={395} w={35} h={28} value={cabineQuadrupla[2]} label="3" />
      <Bed x={262} y={427} w={35} h={28} value={cabineQuadrupla[1]} label="2" />
      <Bed x={303} y={427} w={35} h={28} value={cabineQuadrupla[3]} label="4" />
    </svg>
  );
}

const GenderTag = ({ gender }) => {
  if (!gender) return null;
  const config = {
    M: { bg: "#DBEAFE", color: "#1E40AF", label: "Cabine masculina" },
    F: { bg: "#FCE7F3", color: "#9F1239", label: "Cabine feminina" },
    MIXED: { bg: "#FFEDD5", color: "#9A3412", label: "Cabine mista" },
  }[gender];
  return (
    <span
      className="text-[9px] px-2 py-0.5 font-bold uppercase tracking-wider ml-2"
      style={{ background: config.bg, color: config.color }}
    >
      {config.label}
    </span>
  );
};

const BedAvailability = ({ open, onClose, date, days, occupancy, restrictions, onReserve }) => {
  const [selectedCabin, setSelectedCabin] = useState("");

  useEffect(() => {
    if (open) setSelectedCabin("");
  }, [open, date]);

  if (!open) return null;

  const cabineCasal = occupancy?.cabineCasal || [false, false];
  const suiteTripla = occupancy?.suiteTripla || [false, false, false];
  const suiteCasal = occupancy?.suiteCasal || [false, false];
  const cabineQuadrupla = occupancy?.cabineQuadrupla || [false, false, false, false];
  const restr = restrictions || {};

  const cabins = [
    { key: "Cabine Casal", arr: cabineCasal, total: 2, shared: false },
    { key: "Suíte Tripla", arr: suiteTripla, total: 3, shared: true, restriction: restr.suiteTripla || null },
    { key: "Suíte Casal", arr: suiteCasal, total: 2, shared: false },
    { key: "Cabine Quádrupla · Lado A", arr: [cabineQuadrupla[0], cabineQuadrupla[1]], total: 2, shared: true, hint: "camas 1-2", restriction: restr.cabineQuadruplaA || null },
    { key: "Cabine Quádrupla · Lado B", arr: [cabineQuadrupla[2], cabineQuadrupla[3]], total: 2, shared: true, hint: "camas 3-4", restriction: restr.cabineQuadruplaB || null },
  ].map((c) => {
    const inferred = c.shared ? cabinGenderOf(c.arr) : null;
    // Manual restriction wins over inferred (unless inferred is MIXED which shouldn't happen but keep safe)
    const gender = c.shared ? (c.restriction || inferred) : null;
    return {
      ...c,
      taken: c.arr.filter(Boolean).length,
      gender,
    };
  });
  const totalTaken = cabins.reduce((s, t) => s + t.taken, 0);
  const totalCapacity = 11;
  const free = totalCapacity - totalTaken;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 no-print"
      style={{ background: "rgba(4,11,22,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="bed-availability-modal"
    >
      <div
        className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto thin-scrollbar"
        style={{ background: "#fff", color: "#0F1B33" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "#E5E9F0", background: "#fff" }}>
          <div>
            <div className="text-xs tracking-[0.22em] uppercase font-semibold" style={{ color: "#0066B3" }}>
              Disponibilidade
            </div>
            <div className="text-2xl font-bold mt-1" style={{ letterSpacing: "-0.02em" }} data-testid="modal-date-title">
              {date}
            </div>
            <div className="text-xs mt-1" style={{ color: "#5A6B85" }}>
              Duração: {days} dias · {days - 1} pernoite{days > 2 ? "s" : ""} · Catamarã ZEUS
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center hover:bg-gray-100"
            aria-label="Fechar"
            data-testid="close-modal-btn"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6 p-6">
          <div>
            <div className="text-xs tracking-[0.22em] uppercase font-semibold mb-3" style={{ color: "#0066B3" }}>
              Layout do catamarã
            </div>
            <FloorPlan occupancy={occupancy} />
            <div className="flex items-center gap-4 mt-3 text-xs flex-wrap" style={{ color: "#5A6B85" }}>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3" style={{ background: BED_COLORS.free }} /> Livre
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3" style={{ background: BED_COLORS.male }} /> Masculina
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3" style={{ background: BED_COLORS.female }} /> Feminina
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3" style={{ background: BED_COLORS.occupied }} /> Ocupada
              </span>
            </div>
          </div>

          <div>
            <div
              className="p-5 mb-4"
              style={{
                background: free > 0 ? "linear-gradient(135deg,#F0F7FE,#E1EFFB)" : "linear-gradient(135deg,#FFEBE9,#FFDAD5)",
                borderLeft: `4px solid ${free > 0 ? "#0066B3" : "#E05548"}`,
              }}
            >
              <div className="text-xs tracking-[0.22em] uppercase font-semibold" style={{ color: free > 0 ? "#0066B3" : "#E05548" }}>
                {free > 0 ? "Vagas disponíveis" : "Lotado"}
              </div>
              <div className="text-4xl font-black mt-1" style={{ letterSpacing: "-0.03em", color: free > 0 ? "#0F1B33" : "#8A2F27" }} data-testid="free-count">
                {free}<span className="text-lg font-normal" style={{ color: "#5A6B85" }}> de {totalCapacity}</span>
              </div>
              <div className="text-xs mt-1" style={{ color: "#5A6B85" }}>
                {totalTaken} hóspede{totalTaken !== 1 ? "s" : ""} já confirmado{totalTaken !== 1 ? "s" : ""}
              </div>
            </div>

            {free > 0 && (
              <div className="text-xs tracking-[0.22em] uppercase font-semibold mb-2" style={{ color: "#0066B3" }}>
                Escolha o quarto desejado
              </div>
            )}

            <div className="space-y-2 mb-6">
              {cabins.map((c) => {
                const cabFree = c.total - c.taken;
                const full = cabFree === 0;
                const isSelected = selectedCabin === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => !full && setSelectedCabin(isSelected ? "" : c.key)}
                    disabled={full}
                    className={`w-full flex items-center justify-between p-3 border-2 text-left transition ${full ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:bg-blue-50"}`}
                    style={{
                      borderColor: isSelected ? "#0066B3" : full ? "#F5CFCC" : "#E5E9F0",
                      background: isSelected ? "#F0F7FE" : full ? "#FFF5F4" : "#F9FBFD",
                    }}
                    data-testid={`cabin-select-${c.key}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-5 h-5 flex items-center justify-center border-2 flex-shrink-0"
                        style={{
                          borderColor: isSelected ? "#0066B3" : "#B8C4D8",
                          background: isSelected ? "#0066B3" : "#fff",
                        }}
                      >
                        {isSelected && <Check size={12} color="#fff" strokeWidth={3} />}
                      </div>
                      <div>
                        <div className="text-sm font-bold flex items-center flex-wrap">
                          {c.key}
                          {c.gender && <GenderTag gender={c.gender} />}
                        </div>
                        <div className="text-xs" style={{ color: "#5A6B85" }}>
                          {c.hint && <span className="mr-2 opacity-70">{c.hint} · </span>}
                          {cabFree > 0
                            ? (() => {
                                const gLabel = c.gender && GENDER_LABEL[c.gender]
                                  ? ` ${cabFree > 1 ? GENDER_LABEL[c.gender].many : GENDER_LABEL[c.gender].one}`
                                  : "";
                                return `${cabFree} vaga${cabFree > 1 ? "s" : ""} livre${cabFree > 1 ? "s" : ""}${gLabel}`;
                              })()
                            : "Lotada"}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {c.arr.map((bed, i) => {
                        const { fill } = bedFill(bed);
                        return (
                          <span
                            key={i}
                            className="w-4 h-6 flex items-center justify-center text-[9px] font-bold"
                            style={{ background: fill, color: "#fff" }}
                          >
                            {bed === "M" ? "M" : bed === "F" ? "F" : bed ? "×" : "✓"}
                          </span>
                        );
                      })}
                    </div>
                  </button>
                );
              })}
              {free > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedCabin(selectedCabin === "Sem preferência" ? "" : "Sem preferência")}
                  className="w-full flex items-center gap-3 p-3 border-2 text-left transition hover:bg-blue-50"
                  style={{
                    borderColor: selectedCabin === "Sem preferência" ? "#0066B3" : "#E5E9F0",
                    background: selectedCabin === "Sem preferência" ? "#F0F7FE" : "#fff",
                  }}
                  data-testid="cabin-select-nopref"
                >
                  <div
                    className="w-5 h-5 flex items-center justify-center border-2 flex-shrink-0"
                    style={{
                      borderColor: selectedCabin === "Sem preferência" ? "#0066B3" : "#B8C4D8",
                      background: selectedCabin === "Sem preferência" ? "#0066B3" : "#fff",
                    }}
                  >
                    {selectedCabin === "Sem preferência" && <Check size={12} color="#fff" strokeWidth={3} />}
                  </div>
                  <div>
                    <div className="text-sm font-bold">Sem preferência</div>
                    <div className="text-xs" style={{ color: "#5A6B85" }}>
                      Deixar a equipe da Apecatu escolher a melhor cabine.
                    </div>
                  </div>
                </button>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {free > 0 ? (
                <button
                  onClick={() => onReserve && onReserve(selectedCabin)}
                  disabled={!selectedCabin}
                  className="w-full py-3 flex items-center justify-center gap-2 font-semibold text-sm tracking-wider uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: "#0066B3", color: "#fff" }}
                  data-testid="reserve-from-modal"
                >
                  <Anchor size={16} />
                  {selectedCabin ? `Reservar · ${selectedCabin}` : "Selecione um quarto"}
                </button>
              ) : (
                <a
                  href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
                    `Olá Lucas! A saída ${date} está lotada — posso entrar na lista de espera?`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-3 flex items-center justify-center gap-2 font-semibold text-sm tracking-wider uppercase"
                  style={{ background: "#E05548", color: "#fff" }}
                  data-testid="waiting-list-btn"
                >
                  <MessageCircle size={16} /> Entrar na lista de espera
                </a>
              )}
              <button
                onClick={onClose}
                className="w-full py-3 text-xs tracking-wider uppercase font-semibold border"
                style={{ borderColor: "#E5E9F0", color: "#5A6B85" }}
                data-testid="modal-close-btn"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BedAvailability;
